import { useState } from "react";
import { User } from "@/api/api";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Role } from "@/components/AuthorizationProvider.tsx";

interface UserSelectorProps {
  users: User[];
  selectedUser: User | undefined;
  onSelect: (username: string) => void;
}

export function UserSelector({
  users,
  selectedUser,
  onSelect,
}: UserSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter users based on both username and email
  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();
    return (
      user.username.toLowerCase().includes(search) ||
      (user.email && user.email.toLowerCase().includes(search))
    );
  });

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setSearchTerm("");
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-14 rounded-lg" // Increased height
        >
          {selectedUser ? (
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-start gap-1 text-left">
                <span className="truncate">
                  {selectedUser.email || selectedUser.username}
                </span>
                {selectedUser.email && (
                  <span className="text-xs text-muted-foreground truncate">
                    {selectedUser.username}
                  </span>
                )}
              </div>
              <Badge
                variant="outline"
                className={cn(
                  "ml-auto",
                  selectedUser.role === Role.SuperAdmin &&
                    "bg-red-500/20 text-red-500",
                  selectedUser.role === Role.TenantAdmin &&
                    "bg-blue-500/20 text-blue-500",
                  selectedUser.role === Role.Creator &&
                    "bg-green-500/20 text-green-500",
                )}
              >
                {selectedUser.role}
              </Badge>
            </div>
          ) : (
            <span className="text-muted-foreground">Select user...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[600px] p-0">
        {" "}
        {/* Increased width */}
        <Command
          className="max-h-[400px]" // Increased height for dropdown
        >
          <CommandInput
            placeholder="Search by username or email..."
            onValueChange={setSearchTerm}
            className="h-12" // Taller search box
          />
          <CommandList>
            <CommandEmpty>No user found.</CommandEmpty>
            <CommandGroup heading="Users">
              {filteredUsers.map((user) => (
                <CommandItem
                  key={user.username}
                  value={user.username}
                  onSelect={(value) => {
                    onSelect(value);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between py-3" // More padding
                  keywords={[user.email || user.username]}
                >
                  <div className="flex flex-col items-start">
                    <span>{user.email || user.username}</span>
                    {user.email && (
                      <span className="text-xs text-muted-foreground">
                        {user.username}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        selectedUser?.username === user.username && "ml-auto",
                        user.role === Role.SuperAdmin &&
                          "bg-red-500/20 text-red-500",
                        user.role === Role.TenantAdmin &&
                          "bg-blue-500/20 text-blue-500",
                        user.role === Role.Creator &&
                          "bg-green-500/20 text-green-500",
                      )}
                    >
                      {user.role}
                    </Badge>
                    <Check
                      className={cn(
                        "ml-2 h-4 w-4",
                        selectedUser?.username === user.username
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
